/**
 * Page produit Kloody : comportements légers, sans dépendance.
 *  1. Date de livraison estimée (calculée dans le navigateur, jamais figée par le cache Shopify).
 *  2. Mise à jour des blocs Kloody au changement de variante (événement `variant:update` du thème).
 *  3. Nom de l'option sélectionnée affiché immédiatement.
 *  4. Barre d'achat mobile (visible quand le bouton principal a défilé hors de l'écran).
 *  5. Défilement doux vers l'ancre des avis.
 *  6. Panier en tiroir : date de livraison estimée recalculée quand le tiroir est mis à jour.
 */
(() => {
  if (window.__klProductLoaded) return;
  window.__klProductLoaded = true;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ */
  /* 1. Livraison estimée                                                */
  /* ------------------------------------------------------------------ */

  /** @param {string | undefined} value @returns {number | null} */
  const toDays = (value) => {
    const n = parseInt(value ?? '', 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };

  /**
   * Lit les jours exclus : « JJ/MM » (chaque année) ou « JJ/MM/AAAA » (une année), séparés par saut de ligne, espace ou virgule.
   * @param {string | undefined} raw
   */
  const parseExcludedDates = (raw) => {
    const yearly = new Set();
    const once = new Set();
    (raw ?? '').split(/[\s,;]+/).forEach((token) => {
      const match = token.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?$/);
      if (!match) return;
      const day = match[1].padStart(2, '0');
      const month = match[2].padStart(2, '0');
      if (match[3]) once.add(`${match[3]}-${month}-${day}`);
      else yearly.add(`${month}-${day}`);
    });
    return { yearly, once };
  };

  const pad = (n) => String(n).padStart(2, '0');

  /** @param {HTMLElement} el */
  const updateEstimate = (el) => {
    const dispatch = toDays(el.dataset.dispatch) ?? 0;
    let min = toDays(el.dataset.min);
    let max = toDays(el.dataset.max);
    if (min === null && max === null) return;
    if (min === null) min = max;
    if (max === null) max = min;
    if (min > max) [min, max] = [max, min];

    const skipSat = el.dataset.skipSat === 'true';
    const skipSun = el.dataset.skipSun === 'true';
    const excluded = parseExcludedDates(el.dataset.holidays);

    const isExcluded = (d) =>
      (skipSat && d.getDay() === 6) ||
      (skipSun && d.getDay() === 0) ||
      excluded.yearly.has(`${pad(d.getMonth() + 1)}-${pad(d.getDate())}`) ||
      excluded.once.has(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);

    const nextValidDay = (from) => {
      const d = new Date(from);
      for (let i = 0; i < 400 && isExcluded(d); i++) d.setDate(d.getDate() + 1);
      return d;
    };

    // Ajoute `count` jours comptés (les jours exclus sont sautés).
    const addCountedDays = (from, count) => {
      const d = new Date(from);
      let left = count;
      for (let i = 0; i < 4000 && left > 0; i++) {
        d.setDate(d.getDate() + 1);
        if (!isExcluded(d)) left--;
      }
      return d;
    };

    const shipDate = addCountedDays(nextValidDay(new Date()), dispatch);
    const earliest = addCountedDays(shipDate, min);
    const latest = addCountedDays(shipDate, max);

    let format;
    try {
      format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });
    } catch {
      format = new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
    }

    el.querySelectorAll('[data-kl-date]').forEach((node) => {
      node.textContent = format.format(node.dataset.klDate === 'min' ? earliest : latest);
    });
    el.hidden = false;
  };

  const initEstimates = () => document.querySelectorAll('[data-kl-eta]').forEach(updateEstimate);

  /* ------------------------------------------------------------------ */
  /* 4. Barre d'achat mobile                                             */
  /* ------------------------------------------------------------------ */

  /** @type {IntersectionObserver | undefined} */
  let stickyObserver;

  const mainAddButton = () => document.querySelector('[data-kl-main-atc] button[name="add"]');

  const syncStickyState = () => {
    const bar = document.querySelector('[data-kl-sticky]');
    const addButton = bar?.querySelector('[data-kl-sticky-add]');
    const main = mainAddButton();
    if (addButton && main) addButton.disabled = main.hasAttribute('disabled');
  };

  const initSticky = () => {
    stickyObserver?.disconnect();
    const bar = document.querySelector('[data-kl-sticky]');
    const target = document.querySelector('[data-kl-main-atc]');
    if (!bar || !target || !('IntersectionObserver' in window)) return;

    stickyObserver = new IntersectionObserver(([entry]) => {
      // Visible seulement quand le bouton principal est passé AU-DESSUS de l'écran.
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', scrolledPast);
    });
    stickyObserver.observe(target);
    syncStickyState();
  };

  /* ------------------------------------------------------------------ */
  /* 2. Mise à jour au changement de variante                            */
  /* ------------------------------------------------------------------ */

  document.addEventListener('variant:update', (event) => {
    const html = event.detail?.data?.html;
    if (!html) return;

    document.querySelectorAll('[data-kl-live]').forEach((el) => {
      const next = html.querySelector(`[data-kl-live="${CSS.escape(el.dataset.klLive)}"]`);
      if (!next) return;
      if (el.innerHTML !== next.innerHTML) el.innerHTML = next.innerHTML;
      el.hidden = next.hidden;
      if (next.dataset.state) el.dataset.state = next.dataset.state;
    });

    initEstimates();
    syncStickyState();
    // Le thème peut remplacer le bouton principal : on ré-observe pour rester fiable.
    requestAnimationFrame(initSticky);
  });

  /* ------------------------------------------------------------------ */
  /* 3. Nom de l'option sélectionnée, immédiat                           */
  /* ------------------------------------------------------------------ */

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const output = input.closest('.kl-option')?.querySelector('[data-kl-selected]');
    if (output) output.textContent = input.value;
  });

  /* ------------------------------------------------------------------ */
  /* Clics : barre d'achat mobile et ancre vers les avis                 */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-kl-sticky-add]')) {
      const main = mainAddButton();
      if (main && !main.hasAttribute('disabled')) main.click();
      return;
    }

    const anchor = event.target.closest('a[data-kl-anchor]');
    if (!anchor) return;
    const id = anchor.getAttribute('href')?.slice(1);
    const destination = id ? document.getElementById(id) : null;
    if (!destination) return;

    event.preventDefault();
    destination.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
    destination.setAttribute('tabindex', '-1');
    destination.focus({ preventScroll: true });
  });

  /* ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------ */
  /* Panier en tiroir : la date estimée est recalculée quand son contenu est remplacé */
  /* ------------------------------------------------------------------ */

  let cartTimer;

  const watchCartDrawer = () => {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer || drawer.dataset.klWatched) return;
    drawer.dataset.klWatched = 'true';

    new MutationObserver(() => {
      clearTimeout(cartTimer);
      cartTimer = setTimeout(() => {
        if (drawer.querySelector('[data-kl-eta][hidden]')) initEstimates();
      }, 50);
    }).observe(drawer, { childList: true, subtree: true });
  };

  const init = () => {
    initEstimates();
    initSticky();
    watchCartDrawer();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Éditeur de thème : ré-initialise quand une section est rechargée.
  document.addEventListener('shopify:section:load', init);
})();
